import React from "react"
import { Box } from "@chakra-ui/react"

import BrokerGuidancePanel, { useBrokerGuidanceColors } from "./BrokerGuidancePanel"
import {
  ChecklistRow,
  GuidanceAccordionBody,
  GuidanceBlock,
  GuidanceIntro,
} from "./ibkrGuidanceShared"

export interface HyperliquidCsvImportGuidanceProps {
  compact?: boolean
  /** When false, omit Tessera account-name copy (demo has no account chrome). */
  showAccountNotes?: boolean
}

/**
 * Hyperliquid Trade History CSV: see Docs/help-centre/34-connect-hyperliquid.md
 */
const HyperliquidCsvImportGuidance = ({
  compact = false,
  showAccountNotes = true,
}: HyperliquidCsvImportGuidanceProps): React.ReactElement => {
  const colors = useBrokerGuidanceColors()

  return (
    <BrokerGuidancePanel
      title="How to export Hyperliquid Trade History"
      compact={compact}
      collapsible
    >
      <GuidanceAccordionBody>
        <GuidanceIntro color={colors.bodyColor} className="!mb-0">
          Upload the{" "}
          <Box as="span" fontWeight="semibold">
            Trade History
          </Box>{" "}
          CSV from Hyperliquid, not Funding History or a deposits/withdrawals export. Asset class
          is always crypto.
        </GuidanceIntro>

        <GuidanceBlock title="Export steps">
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Open{" "}
            <Box as="span" fontWeight="semibold">
              app.hyperliquid.xyz
            </Box>{" "}
            and connect the wallet you traded with
          </ChecklistRow>
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Open{" "}
            <Box as="span" fontWeight="semibold">
              Portfolio
            </Box>{" "}
            (or the lower trade panel) →{" "}
            <Box as="span" fontWeight="semibold">
              Trade History
            </Box>{" "}
            tab
          </ChecklistRow>
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Choose the range you need (or all available history)
          </ChecklistRow>
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Click{" "}
            <Box as="span" fontWeight="semibold">
              Export
            </Box>{" "}
            /{" "}
            <Box as="span" fontWeight="semibold">
              Export to CSV
            </Box>{" "}
            and save the file
          </ChecklistRow>
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Upload the downloaded CSV here
          </ChecklistRow>
        </GuidanceBlock>

        <GuidanceBlock title="Correct export">
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Fill fields such as time, coin, direction, price, size, and closed PnL
          </ChecklistRow>
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Hyperliquid exports can be capped around{" "}
            <Box as="span" fontWeight="semibold">
              10,000
            </Box>{" "}
            rows. Export regularly if you trade heavily so older fills are not dropped
          </ChecklistRow>
        </GuidanceBlock>

        <GuidanceBlock title="Timezone">
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Set timezone to the IANA zone that matches the export&apos;s time column (confirm against
            the UI / your locale)
          </ChecklistRow>
        </GuidanceBlock>

        {showAccountNotes ? (
          <GuidanceBlock title="Account in Tessera">
            <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
              Enter an{" "}
              <Box as="span" fontWeight="semibold">
                account name
              </Box>{" "}
              for how this wallet appears in Tessera. Re-importing the same name adds or updates
              fills.
            </ChecklistRow>
          </GuidanceBlock>
        ) : null}
      </GuidanceAccordionBody>
    </BrokerGuidancePanel>
  )
}

export default HyperliquidCsvImportGuidance
